import { Reducer } from "redux";

import { SpintrActionTypes } from "src/spintr/action-types";
import { SpintrTypes } from "src/typings";
import { SocialFeedActionTypes } from "./action-types";
import {
    IFetchSocialFeedFulfilledAction,
    IFetchSocialFeedPendingAction,
    SocialFeedAction,
} from "./actions";
import {
    ICombinedFeedParams,
    IGeneralFeedParams,
    IGroupFeedParams,
    ISystemStatusFeedParams,
    IUserFeedParams,
    SocialFeedType
} from "./types";

export interface ICommonFeedState<TFeedRequest> {
    hasMore: boolean;
    isLoading: boolean;
    lastFetched?: Date;
    lastParams?: TFeedRequest;
    entries: Spintr.ISocialPostBase[];
}

export interface ICombinedFeedState extends ICommonFeedState<ICombinedFeedParams> {
}

export interface IGeneralFeedState extends ICommonFeedState<IGeneralFeedParams> {
}

export interface IUserFeedState extends ICommonFeedState<IUserFeedParams> {
}

export interface IGroupFeedState extends ICommonFeedState<IGroupFeedParams> {
}

export interface ISystemStatusFeedState extends ICommonFeedState<ISystemStatusFeedParams> {
}

export interface ISocialFeedState {
    combined: ICombinedFeedState;
    general: IGeneralFeedState;
    profile: IUserFeedState;
    group: IGroupFeedState;
    systemStatus: ISystemStatusFeedState;
    filters?: any;
}

const initialState: ISocialFeedState = {
    combined: {
        entries: [],
        hasMore: true,
        isLoading: false,
    },
    general: {
        entries: [],
        hasMore: true,
        isLoading: false,
    },
    profile: {
        entries: [],
        hasMore: true,
        isLoading: false,
    },
    group: {
        entries: [],
        hasMore: true,
        isLoading: false,
    },
    systemStatus: {
        entries: [],
        hasMore: true,
        isLoading: false,
    }
};

type EntryMerger = (
    statePosts: Spintr.ISocialPostBase[],
    newPosts: Spintr.ISocialPostBase[],
) => Spintr.ISocialPostBase[];

const mergeEntries: EntryMerger = (statePosts, newPosts) => {
    let posts = statePosts.slice();

    newPosts.forEach((post) => {
        if (posts.some((p) => p.Id === post.Id)) {
            return;
        }

        posts.push(post);
    });

    posts = posts.sort((a, b) => {
        if (a.isPinned === b.isPinned) {
            return a.Date > b.Date ? -1 : 1;
        }

        return a.isPinned > b.isPinned ? -1 : 1;
    });

    return posts;
}

type SocialFeedPendingHandler = (
    action: IFetchSocialFeedPendingAction,
    state: ISocialFeedState,
) => ISocialFeedState;

const handleSocialFeedPending: SocialFeedPendingHandler = (action, state) => {
    switch (action.meta.feed) {
        case SocialFeedType.Combined:
            return {
                ...state,
                combined: {
                    ...state.combined,
                    entries: action.meta.params.isSilentFetch || action.meta.params.skip > 0 ?
                        state.combined.entries :
                        [],
                    isLoading: action.meta.params.isSilentFetch ? false : true,
                    lastParams: action.meta.params,
                },
            };

        case SocialFeedType.Profile:
            return {
                ...state,
                profile: {
                    ...state.profile,
                    isLoading: true,
                    lastParams: action.meta.params
                },
            };

        case SocialFeedType.General:
            return {
                ...state,
                general: {
                    ...state.general,
                    isLoading: true,
                    lastParams: action.meta.params
                },
            };

        case SocialFeedType.Group:
            return {
                ...state,
                group: {
                    ...state.group,
                    entries: action.meta.params.skip === 0 ? [] : state.group.entries,
                    isLoading: true,
                    lastParams: action.meta.params
                },
            };

        case SocialFeedType.SystemStatus:
            return {
                ...state,
                systemStatus: {
                    ...state.systemStatus,
                    entries: action.meta.params.skip === 0 ? [] : state.systemStatus.entries,
                    isLoading: true,
                    lastParams: action.meta.params,
                },
            };

        default:
            return state;
    }
};

type SocialFeedFulfilledHandler = (
    action: IFetchSocialFeedFulfilledAction,
    state: ISocialFeedState,
) => ISocialFeedState;

const handleSocialFeedFulfilled: SocialFeedFulfilledHandler = (action, state) => {
    const hasMore = action.payload.headers["x-has-more"] === "true";

    action.payload.data = action.payload.data.map((post) => ({
        ...post,
        Date: new Date(post.Date),
    }));

    switch (action.meta.feed) {
        case SocialFeedType.Combined:
            return {
                ...state,
                combined:{
                    ...state.combined,

                    entries: action.meta.params.skip > 0 ?
                        mergeEntries(
                            state.combined.entries,
                            action.payload.data
                        ) :
                        action.payload.data,
                    hasMore,
                    isLoading: false,
                },
            };

        case SocialFeedType.Profile:
            return {
                ...state,
                profile: {
                    ...state.profile,

                    entries: action.meta.params.skip === 0 ?
                        action.payload.data :
                        mergeEntries(
                            state.profile.entries,
                            action.payload.data
                        ),
                    hasMore,
                    isLoading: false,
                },
            };

        case SocialFeedType.General:
            return {
                ...state,
                general: {
                    ...state.general,

                    entries: mergeEntries(
                        state.general.entries,
                        action.payload.data
                    ),
                    hasMore,
                    isLoading: false,
                },
            };

        case SocialFeedType.Group:
            return {
                ...state,
                group: {
                    ...state.group,
                    entries: mergeEntries(state.group.entries, action.payload.data),
                    hasMore,
                    isLoading: false,
                },
            };

        case SocialFeedType.SystemStatus:
            return {
                ...state,
                systemStatus: {
                    ...state.systemStatus,

                    entries: mergeEntries(
                        state.systemStatus.entries,
                        action.payload.data
                    ),
                    hasMore,
                    isLoading: false,
                },
            };

        default:
            return state;
    }
};

function deletePost<TType>(id: number, feed: ICommonFeedState<TType>): ICommonFeedState<TType> {
    if (!feed.entries.length) {
        return feed;
    }

    const postIndex = feed.entries.findIndex(
        (entry) => entry.Id === id
    );

    if (postIndex === -1) {
        return feed;
    }

    const entries = feed.entries.slice();
    entries.splice(postIndex, 1);

    return {
        ...feed,

        entries,
    };
}

function pinPost<TType>(id: number, value: boolean, feed: ICommonFeedState<TType>): ICommonFeedState<TType> {
    if (!feed.entries.length) {
        return feed;
    }

    const postIndex = feed.entries.findIndex(
        (entry) => entry.Id === id
    );

    if (postIndex === -1) {
        return feed;
    }

    let entries = feed.entries.map((e: any) => {
        if (e.Id === id) {
            return {
                ...e,
                isPinned: value
            };
        } else {
            return e;
        }
    });

    entries = entries.sort((a, b) => {
        if (a.isPinned === b.isPinned) {
            return a.Date > b.Date ? -1 : 1;
        }

        return a.isPinned > b.isPinned ? -1 : 1;
    });

    return {
        ...feed,
        entries
    }
}

function setFavorite<TType>(id: number, state: boolean, feed: ICommonFeedState<TType>): ICommonFeedState<TType> {
    if (!feed.entries.length) {
        return feed;
    }

    const postIndex = feed.entries.findIndex(
        (entry) => entry.Id === id
    );

    if (postIndex === -1) {
        return feed;
    }

    return {
        ...feed,

        entries: [
            ...feed.entries.slice(0, postIndex),
            {
                ...feed.entries[postIndex],

                isFavourited: state,
            },
            ...feed.entries.slice(postIndex + 1)
        ]
    };
};

function setFollowing<TType>(id: number, state: boolean, feed: ICommonFeedState<TType>): ICommonFeedState<TType> {
    if (!feed.entries.length) {
        return feed;
    }

    const postIndex = feed.entries.findIndex(
        (entry) => entry.Id === id
    );

    if (postIndex === -1) {
        return feed;
    }

    return {
        ...feed,

        entries: [
            ...feed.entries.slice(0, postIndex),
            {
                ...feed.entries[postIndex],

                isFollowing: state,
            },
            ...feed.entries.slice(postIndex + 1)
        ]
    };
};


function handlePollVote<TType>(item: Spintr.IPollVoteMessage, feed: ICommonFeedState<TType>): ICommonFeedState<TType> {
    if (!feed.entries.length) {
        return feed;
    }

    return {
        ...feed,
        entries: feed.entries.map((entry) => {
            if (!entry.content.find(x => x.Id === item.pollId)) {
                return entry;
            }

            return {
                ...entry,
                content: entry.content.map((c) => {
                    if (c.Id !== item.pollId) {
                        return c;
                    }

                    return {
                        ...c,
                        options: c.options.map((o) => {
                            return {
                                ...o,
                                answers: (item.value && item.optionId === o.id ?
                                    [...o.answers, item.userId] :
                                    [...o.answers].filter(x => x !== item.userId)).filter((answer, index, answers) => answers.indexOf(answer) === index)
                            }
                        })
                    }
                })
            }
        })
    }
};

function verifyQuestion<TType>(meta: any, state: boolean, feed: ICommonFeedState<TType>): ICommonFeedState<TType> {
    if (!feed.entries.length) {
        return feed;
    }

    const postIndex = feed.entries.findIndex(
        (entry) => entry.Id === meta.id
    );

    if (postIndex === -1) {
        return feed;
    }

    return {
        ...feed,

        entries: [
            ...feed.entries.slice(0, postIndex),
            {
                ...feed.entries[postIndex],
                StatusType: meta.value ? 6 : 0
            },
            ...feed.entries.slice(postIndex + 1)
        ]
    };
};

export const SocialFeedReducer: Reducer<ISocialFeedState, SocialFeedAction> = (state = initialState, action): ISocialFeedState => {
    switch (action.type) {
        case SocialFeedActionTypes.ClearFeed:
            // @ts-ignore feedType exists in the response but not in type
            switch (action.meta.feedType) {
                case SocialFeedType.Combined:
                    return {
                        ...state,
                        combined: {
                            ...state.combined,
                            entries: [],
                            hasMore: false,
                            isLoading: false,
                            lastParams: null,
                        },
                    };

                case SocialFeedType.Profile:
                    return {
                        ...state,
                        profile: {
                            ...state.profile,
                            entries: [],
                            hasMore: false,
                            isLoading: false,
                            lastParams: null
                        },
                    };

                case SocialFeedType.General:
                    return {
                        ...state,
                        general: {
                            ...state.general,
                            entries: [],
                            hasMore: false,
                            isLoading: false,
                            lastParams: null
                        },
                    };

                case SocialFeedType.Group:
                    return {
                        ...state,
                        group: {
                            ...state.group,
                            entries: [],
                            hasMore: false,
                            isLoading: false,
                            lastParams: null
                        },
                    };

                case SocialFeedType.SystemStatus:
                    return {
                        ...state,
                        systemStatus: {
                            ...state.systemStatus,
                            entries: [],
                            hasMore: false,
                            isLoading: false,
                            lastParams: null
                        },
                    };
                default:
                    return state;
            }

        case SocialFeedActionTypes.SetFilters:
            return {
                ...state,
                filters: action.data,
            };

        case SocialFeedActionTypes.FetchSocialFeedPending:
            return handleSocialFeedPending(action, state);

        case SocialFeedActionTypes.FetchSocialFeedFulfilled:
            return handleSocialFeedFulfilled(action, state);

        case SocialFeedActionTypes.LocalPostCreated:
        case SocialFeedActionTypes.RemotePostCreated:
            if (!action.post) {
                return state;
            }

            action.post.Date = new Date(action.post.Date);

            if (action.post.feedOwnerId &&
                state.profile.lastParams &&
                state.profile.lastParams.feedId &&
                action.post.feedOwnerId.toString() === state.profile.lastParams.feedId.toString()) {
                return {
                    ...state,
                    profile: {
                        ...state.profile,
                        entries: mergeEntries(
                            state.profile.entries,
                            [action.post]
                        ),
                    },
                };
            }

            if (action.post.FeedId &&
                state.group.lastParams &&
                state.group.lastParams.feedId &&
                action.post.FeedId === state.group.lastParams.feedId) {
                return {
                    ...state,
                    group: {
                        ...state.group,
                        entries: mergeEntries(
                            state.group.entries,
                            [action.post]
                        ),
                    },
                };
            }

            if (action.post.FeedId &&
                state.systemStatus.lastParams &&
                (
                    !state.systemStatus.lastParams.feedId ||
                    action.post.FeedId === state.systemStatus.lastParams.feedId
                )) {
                return {
                    ...state,
                    systemStatus: {
                        ...state.systemStatus,
                        entries: mergeEntries(
                            state.systemStatus.entries,
                            [action.post],
                        ),
                    }
                };
            }

            if (
                (action.post.feedType !== SpintrTypes.FeedType.Group || action.enableProjectPostsOnStartpage) &&
                !action.post.restrictToTargetFeed &&
                (action.post.feedType !== SpintrTypes.FeedType.SystemStatus ||
                    (
                        action.post.content.find(
                            (c) => c.Type === SpintrTypes.UberType.SystemStatus
                        ) as Spintr.ISocialSystemStatus
                    )?.showOnMainFeed)
            ) {
                return {
                    ...state,
                    combined: {
                        ...state.combined,
                        entries: mergeEntries(
                            state.combined.entries,
                            [action.post]
                        ),
                    },
                };
            }
            
            return state;

        case SocialFeedActionTypes.RemotePostUpdated:
            return {
                ...state,
                combined: {
                    ...state.combined,
                    entries: state.combined.entries.map(e => {
                        if (e.Id === action.post.Id) {
                            return {
                                ...e,
                                ...action.post
                            };
                        } else if (e.content.find(x => x.Id === action.post.Id)) {
                            return {
                                ...e,
                                content: e.content.map(c => {
                                    if (c.Id === action.post.Id) {
                                        return {
                                            ...c,
                                            ...action.post
                                        };
                                    } else {
                                        return c;
                                    }
                                })
                            }
                        } else {
                            return e;
                        }
                    })
                },
                general: {
                    ...state.general,

                    entries: state.general.entries.map(e => {
                        if (e.Id === action.post.Id) {
                            return {
                                ...e,
                                ...action.post
                            };
                        } else {
                            return e;
                        }
                    })
                },
                profile: {
                    ...state.profile,

                    entries: state.profile.entries.map(e => {
                        if (e.Id === action.post.Id) {
                            return {
                                ...e,
                                ...action.post
                            };
                        } else {
                            return e;
                        }
                    })
                },
                group: {
                    ...state.group,

                    entries: state.group.entries.map(e => {
                        if (e.Id === action.post.Id) {
                            return {
                                ...e,
                                ...action.post
                            };
                        } else {
                            return e;
                        }
                    })
                },
                systemStatus: {
                    ...state.systemStatus,

                    entries: state.systemStatus.entries.map(e => {
                        if (e.Id === action.post.Id) {
                            return {
                                ...e,
                                ...action.post
                            };
                        } else {
                            return e;
                        }
                    })
                },
            };

        case SocialFeedActionTypes.RemotePostDeleted:
            return {
                ...state,
                combined: deletePost(action.id, state.combined),
                general: deletePost(action.id, state.general),
                profile: deletePost(action.id, state.profile),
                group: deletePost(action.id, state.group),
                systemStatus: deletePost(action.id, state.systemStatus),
            };

        case SocialFeedActionTypes.RemotePostPinned:
            return {
                ...state,
                combined: pinPost(action.id, action.value, state.combined),
                general: pinPost(action.id, action.value, state.general),
                profile: pinPost(action.id, action.value, state.profile),
                group: pinPost(action.id, action.value, state.group),
                systemStatus: pinPost(action.id, action.value, state.systemStatus),
            };

        case SocialFeedActionTypes.RemoteVerifyQuestion:
        case SocialFeedActionTypes.VerifyQuestionPending:
            return {
                ...state,
                combined: verifyQuestion(action.meta, true, state.combined),
                general: verifyQuestion(action.meta, true, state.general),
                profile: verifyQuestion(action.meta, true, state.profile),
                group: verifyQuestion(action.meta, true, state.group),
                systemStatus: verifyQuestion(action.meta, true, state.systemStatus),
            };

        case SpintrActionTypes.AddToFavoritePending:
            return {
                ...state,
                combined: setFavorite(action.meta, true, state.combined),
                general: setFavorite(action.meta, true, state.general),
                profile: setFavorite(action.meta, true, state.profile),
                group: setFavorite(action.meta, true, state.group),
                systemStatus: setFavorite(action.meta, true, state.systemStatus),
            };

        case SpintrActionTypes.AddToFavoriteRejected:
            return {
                ...state,
                combined: setFavorite(action.meta, false, state.combined),
                general: setFavorite(action.meta, false, state.general),
                profile: setFavorite(action.meta, false, state.profile),
                group: setFavorite(action.meta, false, state.group),
                systemStatus: setFavorite(action.meta, false, state.systemStatus),
            };

        case SpintrActionTypes.RemoveFromFavoritesPending:
            return {
                ...state,
                combined: setFavorite(action.meta, false, state.combined),
                general: setFavorite(action.meta, false, state.general),
                profile: setFavorite(action.meta, false, state.profile),
                group: setFavorite(action.meta, false, state.group),
                systemStatus: setFavorite(action.meta, false, state.systemStatus),
            };

        case SpintrActionTypes.RemoveFromFavoritesRejected:
            return {
                ...state,
                combined: setFavorite(action.meta, true, state.combined),
                general: setFavorite(action.meta, true, state.general),
                profile: setFavorite(action.meta, true, state.profile),
                group: setFavorite(action.meta, true, state.group),
                systemStatus: setFavorite(action.meta, true, state.systemStatus),
            };

        case SpintrActionTypes.StartFollowingObjectPending:
            return {
                ...state,
                combined: setFollowing(action.meta, true, state.combined),
                general: setFollowing(action.meta, true, state.general),
                profile: setFollowing(action.meta, true, state.profile),
                group: setFollowing(action.meta, true, state.group),
                systemStatus: setFollowing(action.meta, true, state.systemStatus),
            };

        case SpintrActionTypes.StartFollowingObjectRejected:
            return {
                ...state,
                combined: setFollowing(action.meta, false, state.combined),
                general: setFollowing(action.meta, false, state.general),
                profile: setFollowing(action.meta, false, state.profile),
                group: setFollowing(action.meta, false, state.group),
                systemStatus: setFollowing(action.meta, false, state.systemStatus),
            };

        case SpintrActionTypes.StopFollowingObjectPending:
            return {
                ...state,
                combined: setFollowing(action.meta, false, state.combined),
                general: setFollowing(action.meta, false, state.general),
                profile: setFollowing(action.meta, false, state.profile),
                group: setFollowing(action.meta, false, state.group),
                systemStatus: setFollowing(action.meta, false, state.systemStatus),
            };

        case SpintrActionTypes.StopFollowingObjectRejected:
            return {
                ...state,
                combined: setFollowing(action.meta, true, state.combined),
                general: setFollowing(action.meta, true, state.general),
                profile: setFollowing(action.meta, true, state.profile),
                group: setFollowing(action.meta, true, state.group),
                systemStatus: setFollowing(action.meta, true, state.systemStatus),
            };

        case SocialFeedActionTypes.RemotePollVote:
            return {
                ...state,
                combined: handlePollVote(action.item, state.combined),
                general: handlePollVote(action.item, state.general),
                profile: handlePollVote(action.item, state.profile),
                group: handlePollVote(action.item, state.group),
                systemStatus: handlePollVote(action.item, state.systemStatus),
            };

        default:
            return state;
    }
};